import React, { useEffect, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import setupPolling from 'utilities/setupPolling';

import { useAssignableCompaniesQuery } from 'components/hooks/assignableCompanyQueries';
import { useAssignableCompanyRolesQuery } from 'components/hooks/assignableCompanyRoleQueries';
import useDebounce from 'components/hooks/useDebounce';
import useForm from 'components/hooks/useForm';
import usePersonnelForm from 'components/hooks/usePersonnelForm';
import useProfilePhotoUploading from 'components/hooks/useProfilePhotoUploading';
import useRequestError from 'components/hooks/useRequestError';
import useSidePanel from 'components/hooks/useSidePanel';
import useToggle from 'components/hooks/useToggle';
import useTrackedPersonnel from 'components/hooks/useTrackedPersonnel';
import useValidatedStore from 'components/hooks/useValidatedStore';
import useWindowStorage from 'components/hooks/useWindowStorage';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import DestroyModal from 'components/application/DestroyModal';
import Paginator from 'components/application/Paginator';
import SuccessToast from 'components/application/SuccessToast';
import UndoableToast from 'components/application/UndoableToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import AddPersonnelSection from 'components/personnel/AddPersonnelSection';
import ArchiveModal from 'components/personnel/ArchiveModal';
import BlankPersonnelSearchResults from 'components/personnel/BlankPersonnelSearchResults';
import BlankPersonnelSetSection from 'components/personnel/BlankPersonnelSetSection';
import PersonnelBar from 'components/personnel/PersonnelBar';
import PersonnelFilter from 'components/personnel/PersonnelFilter';
import PersonnelSidePanel from 'components/personnel/PersonnelSidePanel';
import PersonnelTable from 'components/personnel/PersonnelTable';
import ProfilePhotoModal from 'components/personnel/ProfilePhotoModal';
import TrackedPersonnelUsageBanner from 'components/personnel/TrackedPersonnelUsageBanner';
import UnarchiveBlockedModal from 'components/personnel/UnarchiveBlockedModal';

import { sendAnalytics } from 'components/helpers/analytics';
import {
  buildTeamSelectionsFromDomainTeams,
  haveProfilePhotoAttributesChanged,
  personnelParams,
  sortRoleIdsByPosition,
} from 'components/helpers/personnel';
import {
  formatData,
  isLastResourceOnMultiplePageDataset,
} from 'components/helpers/reactQuery';
import { personDisplayName } from 'components/helpers/users';

const defaultTrainingStatusCounts = {
  grey: 0,
  low: 0,
  medium: 0,
  high: 0,
};

const defaultTrainingStatusFilters = {
  greyTrainingFilter: true,
  highTrainingFilter: true,
  lowTrainingFilter: true,
  mediumTrainingFilter: true,
};

const defaultPersonnelMeta = {
  currentPage: null,
  scopedCount: 0,
  totalCount: 0,
  totalPages: null,
  trainingStatusCounts: defaultTrainingStatusCounts,
  unscopedCount: 0,
};

const tabSchema = z.object({
  currentSearch: z.string(),
  isArchivedTab: z.boolean(),
  page: z.number().or(z.null()),
  teamFilter: z.object({
    value: z.string(),
    label: z.string(),
  }),
});

const initialSubcontratorState = { subcontractorName: '' };

export default function PersonnelTab({
  emailFilter,
  initialTrainingFilters,
  onTrainingMatrixButtonClick,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();
  const queryClient = useQueryClient();

  const isTrainingRegisterEnabled =
    currentActor.isAllowedFeature('training_register');

  const isTrainingRegisterOnlyEnabled = currentActor.isAllowedFeature(
    'training_register_only',
  );

  const {
    hasPersonnelEditableAccess,
    hasTeamViewableAccess,
    hasTrainingWriteAccess,
  } = useTrainingRegisterResources();

  const initialCurrentPersonnel = {
    id: '',
    firstName: '',
    lastName: '',
    externalId: '',
    email: '',
    lineManagerId: '',
    company: {
      companyId: currentActor.division.id,
      subcontractorId: '',
    },
    primaryCompanyRoleId: '',
    companyRoleIds: [],
    fieldValues: {},
    isArchived: false,
    isParticipatingInOngoingProjects: false,
    eLearningBookingsCount: 0,
    isEditable: false,
    profileAccessEnabled: false,
    teamSelections: [],
  };

  const defaultPersonnelTypeFilters =
    isTrainingRegisterOnlyEnabled ?
      { employeeFilter: true, subFilter: true }
    : { employeeFilter: true, activeSubFilter: true, inActiveSubFilter: true };

  const sessionStoragePersonnelTypeFilters = JSON.parse(
    window.sessionStorage.getItem('personnelTypeFilters') || null,
  );

  const initialPersonnelTypeFilters =
    sessionStoragePersonnelTypeFilters || defaultPersonnelTypeFilters;

  // local storage
  const [getStore, setStore] = useWindowStorage(`trainingRegister|personnel`, {
    store: window.sessionStorage,
  });

  const [_getPersonnelId, setPersonnelId] = useWindowStorage(
    'personnelTab|personnelId',
    { store: window.sessionStorage },
  );

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.PersonnelTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { t: i18nTComponentsShared } = useTranslation('components', {
    keyPrefix: 'personnel.shared',
  });

  const initialTabStore = {
    isArchivedTab: false,
    currentSearch: '',
    page: 1,
    teamFilter: {
      label: i18nTComponentsShared('allPersonnel'),
      value: 'all_personnel',
    },
  };

  const tabStore = useValidatedStore({
    initialStore: initialTabStore,
    getStore,
    schema: tabSchema,
  });

  const initialTeamFilter = tabStore.teamFilter;

  const locallyPersistedTrainingStatusFilters = JSON.parse(
    window.sessionStorage.getItem('trainingStatusFilters') || null,
  );

  const initialTrainingFilterProvided =
    initialTrainingFilters &&
    Object.values(initialTrainingFilters).filter(Boolean).length > 0;

  const initialTrainingStatusFilters =
    initialTrainingFilterProvided ?
      initialTrainingFilters
    : locallyPersistedTrainingStatusFilters || defaultTrainingStatusFilters;

  const currentPersonnelIdRef = useRef(null);

  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const [isSubcontractor, setIsSubcontractor] = useState(false);

  const [
    sidePanelIsOpen,
    setSidePanelIsOpen,
    openSidePanel,
    closeSidePanel,
    resetSidePanelContext,
    sidePanelContext,
    setSidePanelContext,
  ] = useSidePanel(false, 'new');

  const [
    personnelTypeFilters,
    setPersonnelTypeFilters,
    handlePersonnelTypeFilterChange,
  ] = useForm(initialPersonnelTypeFilters);

  const [
    trainingStatusFilters,
    setTrainingStatusFilters,
    handleTrainingStatusFilterChange,
  ] = useForm(initialTrainingStatusFilters);

  const [teamFilter, setTeamFilter] = useState(initialTeamFilter);

  const [currentPage, setCurrentPage] = useState(1);

  const [currentSearch, setCurrentSearch, handleSearchInputChange] = useForm({
    personnelSearch: tabStore.currentSearch || emailFilter || '',
  });

  const [debouncedCurrentSearch] = useDebounce(currentSearch, 250, {
    onChange: () => setCurrentPage(1),
  });

  const [newCompanyRoles, setNewCompanyRoles] = useForm([]);

  const [currentSubcontractor, setCurrentSubcontractor] = useForm(
    initialSubcontratorState,
  );

  const [
    currentPersonnel,
    setCurrentPersonnel,
    handlePersonnelInputChange,
    handlePersonnelOptionChange,
    handlePersonnelDateChange,
    handleFieldValueInputChange,
    handleDateFieldChange,
    handleFieldOptionChange,
    handleToggle,
    handleProfilePhotoRemoval,
    handleProfilePhotoMediaChange,
    handleNewProfilePhotoUploaded,
    handleProfilePhotoCropReady,
  ] = usePersonnelForm(initialCurrentPersonnel);

  const [isArchivedTab, toggleIsArchivedTab] = useToggle(
    tabStore.isArchivedTab,
  );

  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isDestroyModalOpen, setIsDestroyModalOpen] = useState(false);
  const [isProfilePhotoModalOpen, setIsProfilePhotoModalOpen] = useState(false);

  const {
    isProfilePhotoUploading,
    processProfilePhoto,
    resetProfilePhotoUploadState,
  } = useProfilePhotoUploading({
    getCurrentProfilePhoto: () => currentPersonnel.profilePhoto,
  });

  const [isUnarchiveBlockedModalOpen, setIsUnarchiveBlockedModalOpen] =
    useState(false);

  const [uploadedDomainProfilePhotos, setUploadedDomainProfilePhotos] =
    useState(null);

  const [
    personnelIdsOfProfilePhotoUnderProgress,
    setPersonnelIdsOfProfilePhotoUnderProgress,
  ] = useState(new Set());

  const {
    hasReachedTrackedPersonnelLimit,
    hasReachedTrackedPersonnelUsageThreshold,
    trackedPersonnelUsage,
  } = useTrackedPersonnel();

  const {
    data: domainAssignableCompanies,
    isFetching: isDomainAssignableCompaniesFetching,
    isSuccess: isDomainAssignableCompaniesFetchSuccess,
  } = useAssignableCompaniesQuery();

  const { data: domainAssignableCompanyRoles } =
    useAssignableCompanyRolesQuery();

  const personnelFetchParams = {
    archived: isArchivedTab,
    incl_employees: personnelTypeFilters.employeeFilter,
    page: currentPage,
    search: debouncedCurrentSearch.personnelSearch,
    with_tracked_status: isTrainingRegisterEnabled,
  };

  if (isTrainingRegisterOnlyEnabled) {
    personnelFetchParams.incl_sub = personnelTypeFilters.subFilter;
  } else {
    personnelFetchParams.incl_active_sub = personnelTypeFilters.activeSubFilter;
    personnelFetchParams.incl_inactive_sub =
      personnelTypeFilters.inActiveSubFilter;
  }

  if (teamFilter.value !== 'all_personnel') {
    personnelFetchParams.team = teamFilter.value;
  }

  const isAnyTrainingStatusFilterUnchecked = Object.values(
    trainingStatusFilters,
  ).some((filter) => !filter);

  if (isAnyTrainingStatusFilterUnchecked) {
    personnelFetchParams.statuses = Object.entries(
      trainingStatusFilters,
    ).reduce((acc, [name, checked]) => {
      if (checked) acc.push(name.replace(/TrainingFilter/, ''));
      return acc;
    }, []);
  }

  const {
    data: domainPersonnel,
    isFetching: isDomainPersonnelFetching,
    isLoading: isDomainPersonnelLoading,
    isSuccess: isDomainPersonnelFetchSuccess,
  } = useQuery({
    queryKey: ['personnel', personnelFetchParams],
    queryFn: async () => {
      const response = await axios.get('/personnel', {
        params: personnelFetchParams,
      });

      return response.data;
    },
    keepPreviousData: true,
    select: (data) =>
      formatData({
        data,
        resourceTypes: [
          'companyRole',
          'fieldValue',
          'lineManager',
          'profilePhoto',
          'team',
          'user',
          'userCompanyRole',
        ],
      }),
  });

  const createPersonnel = () => {
    const teamIds = currentPersonnel.teamSelections.map((team) => team.id);
    const managedTeamIds = currentPersonnel.teamSelections
      .filter((team) => team.isManager === true)
      .map((team) => team.id);

    const params = personnelParams({
      currentPersonnel,
      currentSubcontractor,
      isSubcontractor,
      newCompanyRoles,
      profilePhotos: {
        domain: selectedPersonnelPhoto,
        existingFromRelationship: selectedProfilePhoto,
      },
      teamIds,
      managedTeamIds,
    });

    axios
      .post('/personnel', { personnel: params })
      .then((response) => {
        closeSidePanel();

        const resourceLinkPath =
          isTrainingRegisterEnabled ?
            `/personnel/${response.data.data.id}`
          : null;

        const resourceLinkLabel =
          isTrainingRegisterEnabled ?
            i18nT('createPersonnel.success.additionalContent', {
              firstName: response.data.data.attributes.firstName,
              interpolation: { escapeValue: false },
            })
          : null;

        const name = personDisplayName({
          attributes: response.data.data.attributes,
        });

        breadBoard.addToast(
          <SuccessToast
            additionalContent={
              resourceLinkPath && resourceLinkLabel ?
                <div className='tw-pl-8 tw-pt-4'>
                  <OutlinedButton
                    color='grey'
                    href={resourceLinkPath}
                    size='sm'
                  >
                    {resourceLinkLabel}
                  </OutlinedButton>
                </div>
              : undefined
            }
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey={`shared.success.${response.data.meta.userInvited ? 'addedAndSentInvitation' : 'added'}`}
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );

        queryClient.invalidateQueries(['assignableCompanies']);
        queryClient.invalidateQueries(['assignableCompanyRoles']);
        queryClient.invalidateQueries(['companyRoles']);
        queryClient.invalidateQueries(['personnel']);

        resetRequestError();
        resetSidePanelContext();
        resetSubcontractor();
        setCurrentPersonnel(initialCurrentPersonnel);
        setNewCompanyRoles([]);
      })
      .catch(handleRequestError);
  };

  const handlePersonnelUpdateSubmit = () => {
    addPersonnelToPersonnelIdsOfProfilePhotoUnderProgress(currentPersonnel.id);

    const originalPersonnel = domainPersonnel.data.find(
      (personnel) => personnel.id === currentPersonnel.id,
    );

    const originalUserCompanyRoles =
      originalPersonnel.relationships.userCompanyRoles.data.map(
        (userCompanyRole) =>
          domainPersonnel.included.userCompanyRole[userCompanyRole.id],
      );

    const originalCompanyRoleIds =
      originalPersonnel.relationships.companyRoles.data.map((role) => role.id);

    const teamIds = currentPersonnel.teamSelections.map((team) => team.id);
    const managedTeamIds = currentPersonnel.teamSelections
      .filter((team) => team.isManager === true)
      .map((team) => team.id);

    const params = personnelParams({
      currentPersonnel,
      currentSubcontractor,
      fieldValues: currentPersonnel.fieldValues,
      isSubcontractor,
      newCompanyRoles,
      originalCompanyRoleIds,
      originalUserCompanyRoles,
      profilePhotos: {
        domain: selectedPersonnelPhoto,
        existingFromRelationship: selectedProfilePhoto,
      },
      teamIds,
      managedTeamIds,
    });

    axios
      .patch(`/personnel/${currentPersonnel.id}`, { personnel: params })
      .then((response) => {
        closeSidePanel();

        const name = personDisplayName({
          attributes: response.data.data.attributes,
        });

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey={`shared.success.${response.data.meta.userInvited ? 'updatedAndSentInvitation' : 'updated'}`}
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );

        queryClient.invalidateQueries(['assignableCompanies']);
        queryClient.invalidateQueries(['assignableCompanyRoles']);
        queryClient.invalidateQueries(['companyRoles']);
        queryClient.invalidateQueries(['personnel']);

        resetRequestError();
        resetSidePanelContext();
        resetSubcontractor();
        setCurrentPersonnel(initialCurrentPersonnel);
        setNewCompanyRoles([]);

        // there is a current profile photo and there are changes to it
        if (
          currentPersonnel.profilePhoto?.id &&
          (!selectedPersonnelPhoto ||
            haveProfilePhotoAttributesChanged(
              selectedPersonnelPhoto.attributes,
              currentPersonnel.profilePhoto,
            ))
        ) {
          // poll for processed photo
          setupPolling({
            requestFn: () =>
              axios.get(
                `/profile_photos/${currentPersonnel.profilePhoto.hashid}`,
              ),
            testFn: (response) => response.data.data.attributes.processed,
            intervalTime: 1000,
          })
            .startPolling()
            .then((response) => {
              // only update if current personnel is still the same
              if (
                currentPersonnelIdRef.current ===
                response.data.data.relationships.personnel.data.id
              ) {
                handleNewProfilePhotoUploaded(response.data.data);
              }

              removePersonnelFromPersonnelIdsOfProfilePhotoUnderProgress(
                currentPersonnel.id,
              );
            })
            .catch(() => {
              removePersonnelFromPersonnelIdsOfProfilePhotoUnderProgress(
                currentPersonnel.id,
              );
            });
        } else {
          removePersonnelFromPersonnelIdsOfProfilePhotoUnderProgress(
            currentPersonnel.id,
          );
        }
      })
      .catch(handleRequestError);
  };

  const destroyPersonnel = () => {
    axios
      .delete(`/personnel/${currentPersonnel.id}`)
      .then(() => {
        closeSidePanel();

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='shared.success.deleted'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{
                  name: personDisplayName({ attributes: currentPersonnel }),
                }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );

        if (isLastResourceOnMultiplePageDataset({ data: domainPersonnel })) {
          setCurrentPage(currentPage - 1);
        }

        queryClient.invalidateQueries(['companyRoles']);
        queryClient.invalidateQueries(['personnel']);

        resetSidePanelContext();
        resetSubcontractor();
        setCurrentPersonnel(initialCurrentPersonnel);
      })
      .catch(() => {
        closeSidePanel();
        resetSubcontractor();
        setCurrentPersonnel(initialCurrentPersonnel);
      });
  };

  const applyAllFilters = () => {
    setPersonnelTypeFilters(defaultPersonnelTypeFilters);
    setTrainingStatusFilters(defaultTrainingStatusFilters);
  };

  const resetSubcontractor = () => {
    setCurrentSubcontractor(initialSubcontratorState);
    setIsSubcontractor(false);
  };

  const handleArchive = ({
    isToastMessageDisplayed = true,
    personnelId = currentPersonnel.id,
  }) => {
    axios
      .patch(`/personnel/${personnelId}`, {
        personnel: { archived_at: moment().format('DD/MM/YYYY') },
      })
      .then((response) => {
        if (isLastResourceOnMultiplePageDataset({ data: domainPersonnel })) {
          setCurrentPage(currentPage - 1);
        }

        queryClient.invalidateQueries(['companyRoles']);
        queryClient.invalidateQueries(['personnel']);

        if (isToastMessageDisplayed) {
          breadBoard.addToast(
            <UndoableToast
              onBurnToast={breadBoard.handleBurnToast}
              onUndoAction={() =>
                handleUnarchive({
                  personnelId: response.data.data.id,
                  isToastMessageDisplayed: false,
                })
              }
              toastText={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='shared.success.archived'
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    name: personDisplayName({
                      attributes: response.data.data.attributes,
                    }),
                  }}
                />
              }
            />,
          );
        }

        resetSidePanelContext();

        sendAnalytics('Personnel archived', {
          currentUser: currentActor.user,
          personnelId,
        });

        setIsArchiveModalOpen(false);
      })
      .catch(() => {
        setIsArchiveModalOpen(false);

        breadBoard.addInedibleToast({
          fullMessage: i18nTCommon('personnel.errors.notArchived', {
            interpolation: { escapeValue: false },
            name: personDisplayName({ attributes: currentPersonnel }),
          }),
        });
      });
  };

  const handleUnarchive = ({
    isToastMessageDisplayed = true,
    personnelId = currentPersonnel.id,
  }) => {
    axios
      .patch(`personnel/${personnelId}`, { personnel: { archived_at: null } })
      .then((response) => {
        if (isLastResourceOnMultiplePageDataset({ data: domainPersonnel })) {
          setCurrentPage(currentPage - 1);
        }

        queryClient.invalidateQueries(['companyRoles']);
        queryClient.invalidateQueries(['personnel']);

        resetSidePanelContext();

        sendAnalytics('Personnel unarchived', {
          currentUser: currentActor.user,
          personnelId,
        });

        setIsArchiveModalOpen(false);

        if (isToastMessageDisplayed) {
          breadBoard.addToast(
            <UndoableToast
              onBurnToast={breadBoard.handleBurnToast}
              onUndoAction={() =>
                handleArchive({
                  personnelId: response.data.data.id,
                  isToastMessageDisplayed: false,
                })
              }
              toastText={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='shared.success.notArchived'
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    name: personDisplayName({
                      attributes: response.data.data.attributes,
                    }),
                  }}
                />
              }
            />,
          );
        }
      })
      .catch(() => {
        setIsArchiveModalOpen(false);

        breadBoard.addInedibleToast({
          fullMessage: i18nTCommon('personnel.errors.notUnarchived', {
            interpolation: { escapeValue: false },
            name: personDisplayName({ attributes: currentPersonnel }),
          }),
        });
      });
  };

  const handleUnarchiveClick = ({ personnelId }) => {
    handleSelectPersonnel(personnelId);
    handleArchiveButtonClick({ personnelId });
  };

  const handleArchiveButtonClick = ({ personnelId }) => {
    if (!isTrainingRegisterEnabled) return setIsArchiveModalOpen(true);

    const personnelIsBlockedFromUnarchive = () => {
      const personnelToBeUnarchived = domainPersonnel.data.find(
        (personnel) => personnel.id === personnelId,
      );

      const personnelToBeUnarchivedHasOtherBlockers =
        personnelToBeUnarchived.meta.trackableBlockers.some(
          (blocker) => blocker !== 'archived',
        );
      const personnelToBeUnarchivedHasTraining =
        personnelToBeUnarchived.meta.trackableQualities.includes(
          'has_training',
        );

      return (
        personnelToBeUnarchivedHasTraining &&
        !personnelToBeUnarchivedHasOtherBlockers
      );
    };

    if (hasReachedTrackedPersonnelLimit && personnelIsBlockedFromUnarchive()) {
      setIsUnarchiveBlockedModalOpen(true);
    } else {
      setIsArchiveModalOpen(true);
    }
  };

  const openArchiveToggleConfirmation = ({ personnelId }) => {
    setSidePanelIsOpen(false);
    handleArchiveButtonClick({ personnelId });
  };

  // interaction handlers
  const handleNewPersonnel = (event) => {
    event.currentTarget.blur();
    setCurrentPersonnel(initialCurrentPersonnel);
    resetSubcontractor();
    resetRequestError();
    resetSidePanelContext();
    openSidePanel();
  };

  const handleNewCompanyRoleAdd = (newRole) => {
    setNewCompanyRoles(newCompanyRoles.concat([newRole]));
  };

  function handleTeamSelectionRoleChange({ isManager, teamSelection }) {
    setCurrentPersonnel({
      ...currentPersonnel,
      teamSelections: currentPersonnel.teamSelections.map((t) =>
        t.id === teamSelection.id ? { ...t, isManager } : t,
      ),
    });
  }

  function handleTeamSelect(team) {
    const newTeamSelection = {
      id: team.id,
      isManager: false,
      persisted: false,
    };

    setCurrentPersonnel({
      ...currentPersonnel,
      teamSelections: [newTeamSelection, ...currentPersonnel.teamSelections],
    });
  }

  function handleTeamSelectionDelete(teamId) {
    setCurrentPersonnel({
      ...currentPersonnel,
      teamSelections: currentPersonnel.teamSelections.filter(
        (t) => t.id !== teamId,
      ),
    });
  }

  const orderedDomainTeamsForPersonnel = ({ personnel }) =>
    (hasTeamViewableAccess ? personnel.relationships.teams.data : [])
      .map((team) => domainPersonnel.included.team[team.id])
      .sort((a, b) => a.attributes.name.localeCompare(b.attributes.name));

  function handleDivisionOptionChange(selected) {
    const isCompanyIdPersisted =
      String(selected.value.companyId) ===
      selectedPersonnel?.relationships?.company?.data?.id;

    let teamSelections = [];

    if (isCompanyIdPersisted) {
      teamSelections =
        selectedPersonnel ?
          buildTeamSelectionsFromDomainTeams({
            domainTeams: orderedDomainTeamsForPersonnel({
              personnel: selectedPersonnel,
            }),
            user: domainPersonnel.included.user[
              selectedPersonnel.relationships.user.data?.id
            ],
          })
        : [];
    }

    setCurrentPersonnel({
      ...currentPersonnel,
      company: {
        companyId: selected.value.companyId,
        subcontractorId: '',
      },
      teamSelections,
    });
  }

  const handleSearchReset = (event) => {
    const name = event.target.getAttribute('data-attr-name');
    setCurrentSearch({ ...currentSearch, [name]: '' });
  };

  const setFieldValuesOnPersonnel = (selectedPersonnel) => {
    const fieldValues =
      selectedPersonnel.relationships.fieldValues?.data.map(
        (fieldValue) => domainPersonnel.included.fieldValue[fieldValue.id],
      ) || [];

    return fieldValues.reduce((accumulator, fieldValue) => {
      const { id } = fieldValue;
      const { value, valueType } = fieldValue.attributes;
      const { fieldAttribute, fieldOption } = fieldValue.relationships;

      if (fieldAttribute.data) {
        accumulator[fieldAttribute.data.id] = {
          fieldAttributeId: fieldAttribute.data.id,
          fieldOptionId: fieldOption.data?.id,
          id,
          value:
            valueType === 'date' ? moment.parseZone(value).toDate() : value,
          valueType,
        };
      }

      return accumulator;
    }, {});
  };

  const handleSelectPersonnel = (id) => {
    const selectedPersonnel = domainPersonnel.data.find(
      (personnel) => personnel.id === id,
    );

    const { attributes, meta, relationships } = selectedPersonnel;

    const userCompanyRoles = relationships.userCompanyRoles.data.map(
      (userCompanyRole) =>
        domainPersonnel.included.userCompanyRole[userCompanyRole.id],
    );

    const primaryUserCompanyRole = userCompanyRoles.find(
      (userCompanyRole) => userCompanyRole.attributes.primary,
    );

    const user =
      relationships.user.data &&
      domainPersonnel.included.user[relationships.user.data.id];

    const profilePhoto =
      relationships.profilePhoto.data &&
      domainPersonnel.included.profilePhoto[relationships.profilePhoto.data.id];

    currentPersonnelIdRef.current = id;

    const teamSelections = buildTeamSelectionsFromDomainTeams({
      domainTeams: orderedDomainTeamsForPersonnel({
        personnel: selectedPersonnel,
      }),
      user,
    });

    setCurrentPersonnel({
      id,
      firstName: attributes.firstName || '',
      lastName: attributes.lastName || '',
      externalId: attributes.externalId || '',
      email: attributes.email || '',
      lineManagerId: relationships.lineManager.data?.id || '',
      fieldValues: setFieldValuesOnPersonnel(selectedPersonnel),
      company: {
        companyId: relationships.company.data?.id || '',
        subcontractorId: relationships.subcontractor.data?.id || '',
      },
      primaryCompanyRoleId:
        primaryUserCompanyRole?.relationships.companyRole.data.id || '',
      companyRoleIds:
        relationships.companyRoles.data?.map((companyRole) => companyRole.id) ||
        [],
      isArchived: !!attributes.archivedAt,
      isParticipatingInOngoingProjects: meta.isParticipatingInOngoingProjects,
      eLearningBookingsCount: meta.eLearningBookingsCount || 0,
      isEditable: selectedPersonnel.meta.isEditable,
      profileAccessEnabled: !!user?.attributes?.profileAccessEnabled,
      profilePhoto: { ...profilePhoto?.attributes, id: profilePhoto?.id },
      teamSelections,
    });

    resetSubcontractor();
    resetRequestError();
  };

  const handleRowClick = (personnelId) => {
    if (isTrainingRegisterOnlyEnabled || isTrainingRegisterEnabled) {
      setPersonnelId(personnelId);
      window.location.replace(`/personnel/${personnelId}`);
    } else {
      handleShowPersonnel(personnelId);
    }
  };

  const handleShowPersonnel = (personnelId) => {
    handleSelectPersonnel(personnelId);
    setSidePanelContext('show');
    openSidePanel();
  };

  const handleCompanyRolesOptionChange = (values) => {
    if (
      currentPersonnel.primaryCompanyRoleId === '' &&
      currentPersonnel.companyRoleIds.length === 0
    ) {
      setCurrentPersonnel({
        ...currentPersonnel,
        primaryCompanyRoleId: values[0].value,
        companyRoleIds: values.map((v) => v.value),
      });
    } else {
      setCurrentPersonnel({
        ...currentPersonnel,
        companyRoleIds: values.map((v) => v.value),
      });
    }
  };

  function handleSelectPrimary(id) {
    setCurrentPersonnel({ ...currentPersonnel, primaryCompanyRoleId: id });
  }

  function handleRemoveRole(id) {
    const filteredCompanyRoleIds = currentPersonnel.companyRoleIds.filter(
      (roleId) => roleId !== id,
    );

    const newPrimaryCompanyRoleId =
      filteredCompanyRoleIds.length > 0 ?
        sortRoleIdsByPosition(
          domainAssignableCompanyRoles.data,
          filteredCompanyRoleIds,
        )[0]
      : '';

    setCurrentPersonnel({
      ...currentPersonnel,
      companyRoleIds: filteredCompanyRoleIds,
      primaryCompanyRoleId:
        currentPersonnel.primaryCompanyRoleId === id ?
          newPrimaryCompanyRoleId
        : currentPersonnel.primaryCompanyRoleId,
    });
  }

  function handleTeamOptionChange(team) {
    setStore({ ...tabStore, page: 1 });
    setTeamFilter({ value: team.value, label: team.label });
  }

  function handleDeletePersonnelClick() {
    setIsDestroyModalOpen(true);
  }

  function handleSubcontractorSelect(selection) {
    setCurrentSubcontractor(selection);
  }

  const handleProcessProfilePhoto = (tempPhotoFile) => {
    processProfilePhoto(tempPhotoFile, {
      onSuccess: (profilePhotoData) => {
        setUploadedDomainProfilePhotos((previousPhotos) => ({
          ...(previousPhotos || {}),
          [profilePhotoData.id]: profilePhotoData,
        }));
        handleNewProfilePhotoUploaded(profilePhotoData);
        removePersonnelFromPersonnelIdsOfProfilePhotoUnderProgress(
          currentPersonnel.id,
        );
      },
      onError: (error) => {
        removePersonnelFromPersonnelIdsOfProfilePhotoUnderProgress(
          currentPersonnel.id,
        );
        const errorMessage = error?.message || 'Profile photo upload failed';
        breadBoard.addInedibleToast({ fullMessage: errorMessage });
      },
    });
  };

  const handlePersonnelSidePanelCancel = () => {
    closeSidePanel();
    removePersonnelFromPersonnelIdsOfProfilePhotoUnderProgress(
      currentPersonnel.id,
    );
    resetProfilePhotoUploadState();
  };

  const handlePhotoModalOpen = () => {
    setIsProfilePhotoModalOpen(true);
  };

  const handlePhotoModalClose = () => {
    setIsProfilePhotoModalOpen(false);
    resetProfilePhotoUploadState();
    removePersonnelFromPersonnelIdsOfProfilePhotoUnderProgress(
      currentPersonnel.id,
    );
  };

  const addPersonnelToPersonnelIdsOfProfilePhotoUnderProgress = (
    currentPersonnelId,
  ) => {
    const newPersonnelIdsOfProfilePhotoUnderProgress = new Set(
      personnelIdsOfProfilePhotoUnderProgress,
    );
    newPersonnelIdsOfProfilePhotoUnderProgress.add(currentPersonnelId);
    setPersonnelIdsOfProfilePhotoUnderProgress(
      newPersonnelIdsOfProfilePhotoUnderProgress,
    );
  };

  const removePersonnelFromPersonnelIdsOfProfilePhotoUnderProgress = (
    currentPersonnelId,
  ) => {
    const newPersonnelIdsOfProfilePhotoUnderProgress = new Set(
      personnelIdsOfProfilePhotoUnderProgress,
    );
    newPersonnelIdsOfProfilePhotoUnderProgress.delete(currentPersonnelId);
    setPersonnelIdsOfProfilePhotoUnderProgress(
      newPersonnelIdsOfProfilePhotoUnderProgress,
    );
  };

  useEffect(() => {
    setStore({
      currentSearch: debouncedCurrentSearch.personnelSearch,
      isArchivedTab,
      page: currentPage,
      teamFilter,
    });
  }, [
    debouncedCurrentSearch.personnelSearch,
    isArchivedTab,
    currentPage,
    teamFilter,
  ]);

  useEffect(() => {
    if (personnelTypeFilters) {
      window.sessionStorage.setItem(
        'personnelTypeFilters',
        JSON.stringify(personnelTypeFilters),
      );
    }
  }, [personnelTypeFilters]);

  useEffect(() => {
    if (trainingStatusFilters) {
      window.sessionStorage.setItem(
        'trainingStatusFilters',
        JSON.stringify(trainingStatusFilters),
      );
    }
  }, [trainingStatusFilters]);

  const isActionsColumnVisible =
    (isArchivedTab && hasPersonnelEditableAccess) ||
    (isTrainingRegisterEnabled && hasTrainingWriteAccess);

  const areAllFiltersApplied = [
    personnelTypeFilters,
    trainingStatusFilters,
  ].every((filterCollection) =>
    Object.values(filterCollection).every((filterValue) => filterValue),
  );

  const assignableRoles = (domainAssignableCompanyRoles?.data || []).concat(
    newCompanyRoles.map((newCompanyRole) => ({
      id: newCompanyRole.id,
      attributes: { position: newCompanyRole.position },
      type: 'companyRole',
    })),
  );

  const currentPersonnelRoles =
    currentPersonnel.id && domainPersonnel ?
      currentPersonnel.companyRoleIds
        .map((id) => domainPersonnel.included.companyRole[id])
        .sort((a, b) => {
          if (a.id === currentPersonnel.primaryCompanyRoleId) return -1;
          if (b.id === currentPersonnel.primaryCompanyRoleId) return 1;

          return a.attributes.position > b.attributes.position ? 1 : -1;
        })
    : [];

  const selectedPersonnel = domainPersonnel?.data.find(
    (personnel) => personnel.id === currentPersonnel.id,
  );

  const selectedProfilePhoto =
    domainPersonnel && selectedPersonnel?.relationships.profilePhoto.data ?
      domainPersonnel.included.profilePhoto[
        selectedPersonnel.relationships.profilePhoto.data.id
      ]
    : null;

  const domainProfilePhoto =
    domainPersonnel && currentPersonnel.profilePhoto ?
      domainPersonnel.included.profilePhoto[currentPersonnel.profilePhoto.id]
    : null;

  const uploadedProfilePhoto =
    !!uploadedDomainProfilePhotos && !!currentPersonnel?.profilePhoto?.id ?
      uploadedDomainProfilePhotos[currentPersonnel.profilePhoto.id]
    : null;

  const selectedPersonnelPhoto = uploadedProfilePhoto || domainProfilePhoto;

  const personnelMeta = domainPersonnel?.meta || defaultPersonnelMeta;

  const {
    scopedCount,
    totalCount,
    totalPages,
    trainingStatusCounts,
    unscopedCount,
  } = personnelMeta;

  const isDataDisplayable =
    isDomainAssignableCompaniesFetching ||
    !isDomainAssignableCompaniesFetchSuccess ||
    isDomainPersonnelFetching ||
    !isDomainPersonnelFetchSuccess ||
    currentSearch.personnelSearch !== debouncedCurrentSearch.personnelSearch;

  return (
    <>
      <PersonnelFilter
        isDisabled={isArchivedTab}
        personnelTypeFilters={{
          filters: personnelTypeFilters,
          onChange: (event) => {
            handlePersonnelTypeFilterChange(event);
            setCurrentPage(1);
          },
        }}
        scopedCount={scopedCount}
        teamFilter={{
          filter: {
            label:
              domainPersonnel?.included.team[teamFilter.value]?.attributes
                .name || teamFilter.label,
            value: teamFilter.value,
          },
          onChange: handleTeamOptionChange,
        }}
        totalCount={totalCount}
        trainingStatusCounts={
          isArchivedTab ? defaultTrainingStatusCounts : trainingStatusCounts
        }
        trainingStatusFilters={{
          filters: trainingStatusFilters,
          onChange: (event) => {
            handleTrainingStatusFilterChange(event);
            setCurrentPage(1);
          },
        }}
      />
      <div className='col-sm-9 tw-pr-0'>
        {hasReachedTrackedPersonnelUsageThreshold &&
          (['primary', 'administrator'].includes(
            currentActor.user.attributes.accessType,
          ) ||
            hasReachedTrackedPersonnelLimit) && (
            <TrackedPersonnelUsageBanner
              hasReachedLimit={hasReachedTrackedPersonnelLimit}
              usage={trackedPersonnelUsage}
            />
          )}
        {(isDomainPersonnelLoading || unscopedCount > 0) && (
          <PersonnelBar
            isActionButtonVisible={hasPersonnelEditableAccess}
            isArchived={isArchivedTab}
            isDomainPersonnelLoading={isDomainPersonnelLoading}
            onArchiveToggle={() => {
              setCurrentPage(1);
              toggleIsArchivedTab();
            }}
            onNewPersonnel={handleNewPersonnel}
            onSearchInputChange={handleSearchInputChange}
            onSearchReset={handleSearchReset}
            onTrainingMatrixButtonClick={onTrainingMatrixButtonClick}
            personnelSearch={currentSearch.personnelSearch}
          />
        )}
        {isDataDisplayable || domainPersonnel.data.length > 0 ?
          <>
            <PersonnelTable
              domainAssignableCompanies={domainAssignableCompanies}
              domainPersonnel={domainPersonnel}
              isActionsColumnVisible={isActionsColumnVisible}
              isArchived={isArchivedTab}
              isDataDisplayable={isDataDisplayable}
              onRowClick={handleRowClick}
              onUnarchiveClick={handleUnarchiveClick}
              personnelIdsOfProfilePhotoUnderProgress={
                personnelIdsOfProfilePhotoUnderProgress
              }
            />
            {isDomainPersonnelFetchSuccess && totalPages > 1 && (
              <div className='tw-mt-20 tw-text-center'>
                <Paginator
                  currentPage={personnelMeta.currentPage}
                  onClick={(event) =>
                    setCurrentPage(
                      Number(event.currentTarget.getAttribute('data-page')),
                    )
                  }
                  totalPages={totalPages}
                />
              </div>
            )}
            {isDestroyModalOpen && sidePanelContext === 'show' && (
              <DestroyModal
                additionalContent={
                  (
                    currentActor.isAllowedFeature('e_learning') &&
                    currentPersonnel.eLearningBookingsCount > 0
                  ) ?
                    <p className='alert alert-danger tw-m-0 tw-mt-5 tw-p-4'>
                      {i18nTComponentsShared('destroyModal.additionalContent')}
                    </p>
                  : null
                }
                confirmationText={i18nTCommon('shared.deleteName', {
                  interpolation: { escapeValue: false },
                  name: currentPersonnel.firstName,
                })}
                displayText={i18nTCommon('shared.areYouSureYouWantToDelete', {
                  interpolation: { escapeValue: false },
                  name: currentPersonnel.firstName,
                })}
                isOpen={isDestroyModalOpen}
                onClose={() => setIsDestroyModalOpen(false)}
                onDestroy={destroyPersonnel}
              />
            )}
          </>
        : unscopedCount === 0 && hasPersonnelEditableAccess ?
          <AddPersonnelSection onAddClick={handleNewPersonnel} />
        : debouncedCurrentSearch.personnelSearch ?
          <BlankPersonnelSearchResults
            allowAdditionalSearching={currentActor.division.attributes.primary}
            allowFilterChange={!areAllFiltersApplied && !isArchivedTab}
            isArchived={isArchivedTab}
            onFilterInclusionClick={applyAllFilters}
            onSetToggle={toggleIsArchivedTab}
            search={debouncedCurrentSearch.personnelSearch}
          />
        : <BlankPersonnelSetSection isArchived={isArchivedTab} />}
      </div>
      {isDomainAssignableCompaniesFetchSuccess &&
        isDomainPersonnelFetchSuccess && (
          <PersonnelSidePanel
            allowShowFooterContents={
              hasPersonnelEditableAccess && currentPersonnel.isEditable
            }
            assignableCompanies={domainAssignableCompanies.data}
            assignableRoles={assignableRoles}
            closeSidePanel={() => {
              closeSidePanel();
              resetSidePanelContext();
            }}
            createPersonnel={createPersonnel}
            currentPersonnel={currentPersonnel}
            currentSubcontractor={currentSubcontractor}
            division={
              selectedPersonnel &&
              domainAssignableCompanies.data.find(
                (company) =>
                  company.attributes.resourceType === 'Company' &&
                  company.id ===
                    selectedPersonnel.relationships.company.data?.id,
              )
            }
            domainProfilePhoto={selectedPersonnelPhoto}
            domainTeams={domainPersonnel.included.team}
            isProfilePhotoLoading={isProfilePhotoUploading}
            isSubcontractor={isSubcontractor}
            lineManager={
              selectedPersonnel?.relationships.lineManager.data &&
              domainPersonnel.included.lineManager[
                selectedPersonnel.relationships.lineManager.data.id
              ]
            }
            onArchiveButtonClick={openArchiveToggleConfirmation}
            onAssignableRoleAdd={() => {}}
            onCancel={handlePersonnelSidePanelCancel}
            onCompanyRolesOptionChange={handleCompanyRolesOptionChange}
            onDateFieldChange={handleDateFieldChange}
            onDeleteClick={handleDeletePersonnelClick}
            onDivisionOptionChange={handleDivisionOptionChange}
            onEditClick={() => setSidePanelContext('edit')}
            onFieldOptionChange={handleFieldOptionChange}
            onNewCompanyRoleAdd={handleNewCompanyRoleAdd}
            onPersonnelDateChange={handlePersonnelDateChange}
            onPersonnelFieldChange={handleFieldValueInputChange}
            onPersonnelInputChange={handlePersonnelInputChange}
            onPersonnelOptionChange={handlePersonnelOptionChange}
            onPersonnelUpdateSubmit={handlePersonnelUpdateSubmit}
            onPhotoModalOpen={handlePhotoModalOpen}
            onRemoveRole={handleRemoveRole}
            onSelectPrimary={handleSelectPrimary}
            onSubcontractorSelect={handleSubcontractorSelect}
            onTeamSelect={handleTeamSelect}
            onTeamSelectionDelete={handleTeamSelectionDelete}
            onTeamSelectionRoleChange={handleTeamSelectionRoleChange}
            onToggle={handleToggle}
            personnel={selectedPersonnel}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
            roles={currentPersonnelRoles}
            setCurrentPersonnel={setCurrentPersonnel}
            setIsSubcontractor={setIsSubcontractor}
            setSidePanelContext={setSidePanelContext}
            sidePanelContext={sidePanelContext}
            sidePanelIsOpen={sidePanelIsOpen}
            subcontractor={
              selectedPersonnel &&
              domainAssignableCompanies.data.find(
                (assignableCompany) =>
                  assignableCompany.attributes.resourceType ===
                    'Subcontractor' &&
                  assignableCompany.id ===
                    selectedPersonnel.relationships.subcontractor.data?.id,
              )
            }
            submitDisabled={submitDisabled}
            teams={
              hasTeamViewableAccess && selectedPersonnel ?
                selectedPersonnel.relationships.teams.data.map(
                  (team) => domainPersonnel.included.team[team.id],
                )
              : []
            }
            user={
              selectedPersonnel?.relationships?.user?.data &&
              domainPersonnel.included.user[
                selectedPersonnel.relationships.user.data.id
              ]
            }
          />
        )}
      {isArchiveModalOpen && (
        <ArchiveModal
          closeModal={() => setIsArchiveModalOpen(false)}
          hasELearningBookings={
            currentActor.isAllowedFeature('e_learning') &&
            currentPersonnel.eLearningBookingsCount > 0
          }
          isArchived={currentPersonnel.isArchived}
          isOpen={isArchiveModalOpen}
          longName={personDisplayName({ attributes: currentPersonnel })}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
          shortName={currentPersonnel.firstName}
          unarchiveBody={
            <p className='tw-mt-4'>{i18nT('archiveModal.unarchiveBody')}</p>
          }
        />
      )}
      {isUnarchiveBlockedModalOpen && (
        <UnarchiveBlockedModal
          closeModal={() => setIsUnarchiveBlockedModalOpen(false)}
          isOpen={isUnarchiveBlockedModalOpen}
          usage={trackedPersonnelUsage}
        />
      )}
      {isProfilePhotoModalOpen && (
        <ProfilePhotoModal
          currentPersonnel={currentPersonnel}
          domainProfilePhoto={selectedPersonnelPhoto}
          key={currentPersonnel?.id}
          onCropReady={handleProfilePhotoCropReady}
          onMediaLoaded={handleProfilePhotoMediaChange}
          onPhotoModalClose={handlePhotoModalClose}
          onPhotoRemoved={() => {
            handleProfilePhotoRemoval();
            resetProfilePhotoUploadState();
          }}
          onProcessProfilePhoto={(photoFile) => {
            setIsProfilePhotoModalOpen(false);

            // skip it if photo was removed.
            if (photoFile) handleProcessProfilePhoto(photoFile);
          }}
        />
      )}
    </>
  );
}

PersonnelTab.propTypes = {
  emailFilter: PropTypes.string,
  initialTrainingFilters: PropTypes.object,
  onTrainingMatrixButtonClick: PropTypes.func,
};
